import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { useAuth } from './AuthProvider'
import { Experiment } from '../types'
import { supabase } from '../supabaseClient'

interface ActiveExperimentContextValue {
  activeExperiment: Experiment | null
  activeExperimentLoading: boolean
  setActiveExperiment: (exp: Experiment | null) => void
  refreshActiveExperiment: () => Promise<void>
}

const ActiveExperimentContext = createContext<ActiveExperimentContextValue | null>(null)

/**
 * Persists just the experiment ID in localStorage.
 * Fetches the full experiment from Supabase on load and when changed.
 */
export function ActiveExperimentProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const storageKey = user?.id ? `kosmos_active_experiment_id_${user.id}` : null

  const [activeExperiment, setActiveExperimentState] = useState<Experiment | null>(null)
  const [loading, setLoading] = useState(true)

  const fetchExperiment = useCallback(async (id: string): Promise<Experiment | null> => {
    const { data, error } = await supabase
      .from('experiments')
      .select('*, experiment_standards(standard_id, grade)')
      .eq('id', id)
      .single()

    if (error || !data) return null

    const junctionRows = (data.experiment_standards as { standard_id: string; grade: string }[]) ?? []
    return {
      ...data,
      standards: junctionRows.map((r: { standard_id: string }) => r.standard_id),
      grades: Array.from(
        new Set(junctionRows.map((r: { grade: string }) => r.grade).filter(Boolean)),
      ).sort() as string[],
    } as Experiment
  }, [])

  // Load active experiment from stored ID on mount
  useEffect(() => {
    if (!storageKey) {
      setActiveExperimentState(null)
      setLoading(false)
      return
    }
    const storedId = localStorage.getItem(storageKey)
    if (!storedId) {
      setActiveExperimentState(null)
      setLoading(false)
      return
    }
    setLoading(true)
    fetchExperiment(storedId).then((exp) => {
      if (exp) setActiveExperimentState(exp)
      else {
        localStorage.removeItem(storageKey)
        setActiveExperimentState(null)
      }
      setLoading(false)
    })
  }, [storageKey, fetchExperiment])

  const setActiveExperiment = useCallback(
    (exp: Experiment | null) => {
      setActiveExperimentState(exp)
      if (!storageKey) return
      if (exp) localStorage.setItem(storageKey, exp.id)
      else localStorage.removeItem(storageKey)
    },
    [storageKey],
  )

  const refreshActiveExperiment = useCallback(async () => {
    if (!activeExperiment) return
    const fresh = await fetchExperiment(activeExperiment.id)
    if (fresh) setActiveExperimentState(fresh)
  }, [activeExperiment, fetchExperiment])

  return (
    <ActiveExperimentContext.Provider
      value={{ activeExperiment, activeExperimentLoading: loading, setActiveExperiment, refreshActiveExperiment }}
    >
      {children}
    </ActiveExperimentContext.Provider>
  )
}

export function useActiveExperiment(): ActiveExperimentContextValue {
  const ctx = useContext(ActiveExperimentContext)
  if (!ctx) throw new Error('useActiveExperiment must be used inside ActiveExperimentProvider')
  return ctx
}
